import { Link } from 'react-router-dom'
import { useState } from 'react'

import { api } from '../../api/client'
import type { AccessPersona } from '../../api/types'
import { useAsync } from '../../hooks/useAsync'
import { t } from '../../i18n/lingua'
import { Card, EmptyState, ErrorBanner, Spinner, formatDateTime, timeAgo } from '../common'
import { DataTable } from '../DataTable'
import { colonneStatoAccount, etichettaTipoIdentita } from './comuni'

/**
 * «Persone»: the identities behind the account names, one row per person,
 * service account or shared account, unified across domain, local accounts,
 * Linux, Entra ID and network devices. Only who appears in the register: with
 * the non-privileged users off, the privileged ones and nobody else.
 */
export function VistaPersone() {
  const [solo, setSolo] = useState(false)
  const dati = useAsync(() => api.accessPeople(solo), [solo])
  const righe: AccessPersona[] = dati.data?.people ?? []
  return (
    <Card title={t('Persone e account')}>
      <p className="field__hint" style={{ marginTop: 0 }}>
        {t('Una riga per persona: gli account con cui compare nelle fonti (dominio, account locali, Linux, Entra ID, apparati) sono uniti dalle loro chiavi — SID, UPN, nome di dominio — e, per i nomi semplici, dal nome stesso. Un’unione sbagliata si separa dalla scheda della persona; due righe che sono la stessa persona si uniscono da lì.')}
      </p>
      <label className="checkbox" style={{ marginBottom: 8, display: 'inline-flex' }}>
        <input type="checkbox" checked={solo} onChange={(e) => setSolo(e.target.checked)} />{' '}{t('solo chi ha privilegi')}
      </label>
      <ErrorBanner error={dati.error} />
      {dati.loading && !dati.data ? <Spinner /> : righe.length === 0 ? (
        <EmptyState title={t('Nessuna persona nel registro, finora.')} hint={t('Le persone compaiono con i primi eventi delle fonti e con i privilegi letti dall’inventario.')} />
      ) : (
        <DataTable
          rows={righe}
          rowKey={(p) => p.id}
          conteggio={{ nome: t('persone') }}
          initialSort={{ key: 'last_seen', dir: 'desc' }}
          columns={[
            { key: 'display_name', header: t('Nome'), value: (p) => p.display_name, render: (p) => (
              <><Link to={`/access/people/${p.id}`}><strong>{p.display_name}</strong></Link>{p.upn && p.upn !== p.display_name && <><br /><small className="muted">{p.upn}</small></>}</>
            ) },
            { key: 'kind', header: t('Tipo'), filter: 'select', value: (p) => etichettaTipoIdentita(p.kind) },
            { key: 'privileged', header: t('Privilegi'), type: 'number', value: (p) => p.privileges ?? 0, render: (p) => (
              p.privileged ? <span className="badge badge--warn">{t('{n} privilegi', { n: p.privileges ?? 0 })}</span> : <span className="muted">—</span>
            ) },
            { key: 'open_detections', header: t('Rilevamenti aperti'), type: 'number', value: (p) => p.open_detections ?? 0, render: (p) => (
              (p.open_detections ?? 0) > 0 ? <Link to={`/access/detections?persona=${p.id}`} className="badge badge--error">{p.open_detections}</Link> : <span className="muted">0</span>
            ) },
            ...colonneStatoAccount<AccessPersona>(),
            { key: 'last_seen', header: t('Ultimo evento'), type: 'date', value: (p) => p.last_seen ?? '', render: (p) => p.last_seen ? <span title={formatDateTime(p.last_seen)}>{timeAgo(p.last_seen)}</span> : '—' },
            { key: 'last_privileged_logon', header: t('Ultimo accesso privilegiato'), type: 'date', value: (p) => p.last_privileged_logon ?? '', render: (p) => p.last_privileged_logon ? formatDateTime(p.last_privileged_logon) : <span className="muted">—</span> },
            { key: 'on_call', header: t('Reperibile'), filter: 'select', value: (p) => (p.on_call ? t('sì') : t('no')), hidden: true },
            { key: 'keys', header: t('Account'), value: (p) => (p.keys ?? []).join(' '), render: (p) => <small className="muted mono">{(p.keys ?? []).map((k) => k.split(':').slice(1).join(':')).join(', ')}</small>, hidden: true },
          ]}
        />
      )}
    </Card>
  )
}
